<script setup>
/**
 * MyOrdersView
 * Displays all orders for the current user's linked client
 * Only for non-agent users (client role)
 * Features:
 * - Order list in table (desktop) or cards (mobile) format
 * - Click on order to view details in modal
 * - Print and PDF download options from modal
 * - Delete pending orders
 */
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/app/store/auth.js'
import {
  getClientOrders,
  deleteOrder,
  formatOrderNumber,
  getStatusDisplayText,
  getStatusColorClass,
  ORDER_STATUS,
} from '@/core/services/orders.js'
import { ROUTES, STORAGE_KEYS } from '@/core/config/constants.js'
import { formatPrice, formatDateTime } from '@core/utils/formatting.js'
import { useCloudFunctions } from '@/core/composables/useCloudFunctions.js'
import { useToast } from '@/core/composables/useToast.js'
import OrderDetailsModal from '@/ui/components/orders/OrderDetailsModal.vue'

const router = useRouter()
const authStore = useAuthStore()
const { callFunction } = useCloudFunctions()
const { error: showError } = useToast()

// State
const orders = ref([])
const loading = ref(true)
const error = ref(null)
const paymentLoading = ref(false)

// Filters & sorting
const searchQuery = ref('')
const statusFilter = ref('')
const sortKey = ref('createdAt')
const sortDir = ref('desc')

// Order details modal state
const detailsModalOpen = ref(false)
const selectedOrder = ref(null)

// Delete modal state
const deleteModalOpen = ref(false)
const deleteModalOrder = ref(null)
const deleteLoading = ref(false)
const deleteError = ref(null)

// Computed
const clientMakat = computed(() => {
  return authStore.user?.client?.makat || authStore.user?.profile?.client_makat || null
})

const clientName = computed(() => {
  return authStore.user?.client?.name || authStore.user?.profile?.client_name || ''
})

const { isAgent } = storeToRefs(authStore)

// Filtered & sorted orders
const filteredAndSortedOrders = computed(() => {
  let result = orders.value

  // Status filter
  if (statusFilter.value) {
    result = result.filter((o) => o.status === statusFilter.value)
  }

  // Search filter (order number + product names)
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase()
    result = result.filter((o) => {
      const orderNum = formatOrderNumber(o.orderNumber).toLowerCase()
      if (orderNum.includes(q)) return true
      return o.items?.some((item) => item.name?.toLowerCase().includes(q))
    })
  }

  // Sort
  if (sortKey.value) {
    result = [...result].sort((a, b) => {
      let valA, valB
      if (sortKey.value === 'createdAt') {
        valA = a.createdAt?.seconds ?? 0
        valB = b.createdAt?.seconds ?? 0
      } else if (sortKey.value === 'totalWithVat') {
        valA = a.totals?.totalWithVat ?? 0
        valB = b.totals?.totalWithVat ?? 0
      }
      return sortDir.value === 'asc' ? valA - valB : valB - valA
    })
  }

  return result
})

const hasActiveFilters = computed(() => searchQuery.value.trim() || statusFilter.value)

function toggleSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'desc'
  }
}

function clearFilters() {
  searchQuery.value = ''
  statusFilter.value = ''
}

// Load orders
async function loadOrders() {
  if (!clientMakat.value) {
    loading.value = false
    return
  }

  try {
    loading.value = true
    error.value = null
    orders.value = await getClientOrders(clientMakat.value)
  } catch (err) {
    console.error('Failed to load orders:', err)
    error.value = 'שגיאה בטעינת ההזמנות'
  } finally {
    loading.value = false
  }
}

// Navigate back to catalog
function goToCatalog() {
  router.push({ name: ROUTES.CATALOG })
}

// Navigate to profile
function goToProfile() {
  router.push({ name: ROUTES.PROFILE })
}

// Handle payment - generate PayPlus link and redirect
async function handlePayment(order) {
  if (!clientMakat.value) {
    showError('לא קיים לקוח משויך')
    return
  }

  if (order.status !== 'pending') {
    showError('ניתן לשלם רק על הזמנות שממתינות לתשלום')
    return
  }

  try {
    paymentLoading.value = true

    const paymentData = {
      client: {
        vat: authStore.user?.client?.vat,
        makat: clientMakat.value,
        name: clientName.value,
      },
      orderUid: order.id,
      phone: authStore.user?.client?.phone,
      amount: order.totals?.totalWithVat?.toFixed(2),
    }

    const paymentResult = await callFunction('generateOrderPaymentLink', paymentData)

    if (paymentResult?.paymentLink) {
      localStorage.setItem(
        STORAGE_KEYS.PENDING_ORDER,
        JSON.stringify({
          orderId: order.id,
          orderNumber: formatOrderNumber(order.orderNumber),
          clientMakat: clientMakat.value,
          amount: order.totals?.totalWithVat,
          createdAt: new Date().toISOString(),
        })
      )

      window.location.href = paymentResult.paymentLink
    } else {
      showError('לא התקבל קישור לתשלום')
    }
  } catch (err) {
    console.error('Payment error:', err)
    showError(err.message || 'שגיאה ביצירת קישור תשלום')
  } finally {
    paymentLoading.value = false
  }
}

// Order details modal functions
function openDetailsModal(order) {
  selectedOrder.value = order
  detailsModalOpen.value = true
}

function closeDetailsModal() {
  detailsModalOpen.value = false
  selectedOrder.value = null
}

// Delete order functions
function openDeleteModal(order) {
  deleteModalOrder.value = order
  deleteError.value = null
  deleteModalOpen.value = true
}

function closeDeleteModal() {
  deleteModalOpen.value = false
  deleteModalOrder.value = null
  deleteError.value = null
}

async function confirmDelete() {
  if (!deleteModalOrder.value || !clientMakat.value) return

  try {
    deleteLoading.value = true
    deleteError.value = null
    await deleteOrder(clientMakat.value, deleteModalOrder.value.id)
    closeDeleteModal()
    await loadOrders() // Refresh list
  } catch (err) {
    console.error('Failed to delete order:', err)
    deleteError.value = err.message || 'שגיאה במחיקת ההזמנה'
  } finally {
    deleteLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadOrders()
})
</script>

<template>
  <div class="my-orders-view">
    <!-- Header -->
    <header class="orders-header">
      <div class="header-spacer"></div>
      <h1 class="page-title">ההזמנות שלי</h1>
      <button class="back-btn" @click="goToProfile">
        <span class="back-full">חזור לפרופיל</span>
        <span class="back-short">חזור</span>
      </button>
    </header>

    <!-- Client Info Banner -->
    <div v-if="clientName" class="client-banner">
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
      </svg>
      <span>{{ clientName }}</span>
      <span v-if="clientMakat" class="client-makat">({{ clientMakat }})</span>
    </div>

    <!-- Content -->
    <div class="orders-content">
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <span>טוען הזמנות...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <svg
          viewBox="0 0 24 24"
          width="48"
          height="48"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="loadOrders">נסה שוב</button>
      </div>

      <!-- No Client Linked -->
      <div v-else-if="!clientMakat" class="empty-state">
        <svg
          viewBox="0 0 24 24"
          width="64"
          height="64"
          fill="none"
          stroke="currentColor"
          stroke-width="1"
        >
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        <h2>לא משויך ללקוח</h2>
        <p>עליך להיות משויך לחשבון לקוח כדי לצפות בהזמנות.</p>
        <button class="action-btn" @click="goToProfile">לפרופיל</button>
      </div>

      <!-- No Orders -->
      <div v-else-if="orders.length === 0" class="empty-state">
        <svg
          viewBox="0 0 24 24"
          width="64"
          height="64"
          fill="none"
          stroke="currentColor"
          stroke-width="1"
        >
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
        </svg>
        <h2>אין הזמנות</h2>
        <p>עדיין לא בוצעו הזמנות עבור חשבון זה.</p>
        <button class="action-btn" @click="goToCatalog">לקטלוג</button>
      </div>

      <!-- Orders List -->
      <template v-else>
        <!-- Filters Bar -->
        <div class="filters-bar">
          <div class="search-wrapper">
            <svg
              class="search-icon"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              class="filter-input"
              placeholder="חיפוש לפי מספר הזמנה או שם מוצר..."
            />
            <button v-if="searchQuery" class="clear-search" @click="searchQuery = ''">
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="select-wrapper">
            <select v-model="statusFilter" class="filter-select">
              <option value="">הכל</option>
              <option value="pending">ממתין לתשלום</option>
              <option value="paid">שולם</option>
            </select>
            <svg
              class="select-chevron"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <span class="results-count">
            {{ filteredAndSortedOrders.length }} מתוך {{ orders.length }} הזמנות
          </span>
          <button v-if="hasActiveFilters" class="clear-filters-btn" @click="clearFilters">
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            נקה סינון
          </button>
        </div>

        <!-- No Filter Results -->
        <div v-if="filteredAndSortedOrders.length === 0" class="no-filter-results">
          <svg
            viewBox="0 0 24 24"
            width="48"
            height="48"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <p>לא נמצאו הזמנות התואמות את החיפוש</p>
          <button class="action-btn" @click="clearFilters">נקה סינון</button>
        </div>

        <!-- Orders Table (Desktop) -->
        <div v-if="filteredAndSortedOrders.length > 0" class="orders-table-wrapper desktop-only">
          <table class="orders-table">
            <thead>
              <tr>
                <th>מספר הזמנה</th>
                <th
                  class="th-sortable"
                  :class="{ 'th-active': sortKey === 'createdAt' }"
                  @click="toggleSort('createdAt')"
                >
                  תאריך
                  <span class="sort-icon">
                    <svg
                      v-if="sortKey === 'createdAt'"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline
                        :points="sortDir === 'asc' ? '18 15 12 9 6 15' : '6 9 12 15 18 9'"
                      />
                    </svg>
                    <svg
                      v-else
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      opacity="0.3"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </th>
                <th>פריטים</th>
                <th
                  class="th-sortable"
                  :class="{ 'th-active': sortKey === 'totalWithVat' }"
                  @click="toggleSort('totalWithVat')"
                >
                  סה"כ לתשלום
                  <span class="sort-icon">
                    <svg
                      v-if="sortKey === 'totalWithVat'"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline
                        :points="sortDir === 'asc' ? '18 15 12 9 6 15' : '6 9 12 15 18 9'"
                      />
                    </svg>
                    <svg
                      v-else
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      opacity="0.3"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </th>
                <th>סטטוס</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="order in filteredAndSortedOrders"
                :key="order.id"
                class="order-row clickable"
                @click="openDetailsModal(order)"
              >
                <td class="order-number-cell">
                  <span class="order-number">{{ formatOrderNumber(order.orderNumber) }}</span>
                </td>
                <td class="order-date-cell">
                  {{ formatDateTime(order.createdAt) }}
                </td>
                <td class="order-items-cell">
                  <span class="items-count">{{ order.uniqueItemCount }}</span>
                  <span class="items-label">פריטים</span>
                  <span class="unique-count">({{ order.itemCount }} יח')</span>
                </td>
                <td class="order-total-cell">
                  <span class="total-amount">{{ formatPrice(order.totals?.totalWithVat) }}</span>
                  <span class="currency">₪</span>
                </td>
                <td class="order-status-cell">
                  <span class="status-badge" :class="getStatusColorClass(order.status)">
                    {{ getStatusDisplayText(order.status) }}
                  </span>
                </td>
                <td class="order-actions-cell" @click.stop>
                  <div v-if="order.status === 'pending'" class="actions-group">
                    <button
                      class="payment-btn"
                      :disabled="paymentLoading"
                      @click="handlePayment(order)"
                    >
                      <svg
                        v-if="paymentLoading"
                        class="spin"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                      </svg>
                      <svg
                        v-else
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                        <line x1="1" y1="10" x2="23" y2="10" />
                      </svg>
                      <span>{{ paymentLoading ? 'מעבד...' : 'לתשלום' }}</span>
                    </button>
                    <button
                      class="delete-btn"
                      :disabled="paymentLoading"
                      @click="openDeleteModal(order)"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path
                          d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                        />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                      <span>מחק</span>
                    </button>
                  </div>
                  <span v-else class="no-action">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Orders Cards (Mobile) -->
        <div v-if="filteredAndSortedOrders.length > 0" class="orders-cards mobile-only">
          <div
            v-for="order in filteredAndSortedOrders"
            :key="order.id"
            class="order-card clickable"
            @click="openDetailsModal(order)"
          >
            <div class="card-header">
              <span class="order-number">{{ formatOrderNumber(order.orderNumber) }}</span>
              <span class="status-badge" :class="getStatusColorClass(order.status)">
                {{ getStatusDisplayText(order.status) }}
              </span>
            </div>
            <div class="card-body">
              <div class="card-row">
                <span class="card-label">תאריך</span>
                <span class="card-value">{{ formatDateTime(order.createdAt) }}</span>
              </div>
              <div class="card-row">
                <span class="card-label">פריטים</span>
                <span class="card-value">
                  <span class="items-count">{{ order.uniqueItemCount }}</span>
                  פריטים
                  <span class="unique-count">({{ order.itemCount }} יח')</span>
                </span>
              </div>
              <div class="card-row total-row">
                <span class="card-label">סה"כ לתשלום</span>
                <span class="card-value">
                  <span class="total-amount">{{ formatPrice(order.totals?.totalWithVat) }}</span>
                  <span class="currency">₪</span>
                </span>
              </div>
            </div>
            <div v-if="order.status === 'pending'" class="card-footer" @click.stop>
              <button class="payment-btn" :disabled="paymentLoading" @click="handlePayment(order)">
                <svg
                  v-if="paymentLoading"
                  class="spin"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                <span>{{ paymentLoading ? 'מעבד...' : 'עבור לתשלום' }}</span>
              </button>
              <button class="delete-btn" :disabled="paymentLoading" @click="openDeleteModal(order)">
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path
                    d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                  />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
                <span>מחק</span>
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Order Details Modal -->
    <OrderDetailsModal
      v-if="selectedOrder"
      :order="selectedOrder"
      :visible="detailsModalOpen"
      :payment-loading="paymentLoading"
      @close="closeDetailsModal"
      @pay="handlePayment"
    />

    <!-- Delete Confirmation Modal -->
    <div v-if="deleteModalOpen" class="modal-overlay" @click.self="closeDeleteModal">
      <div class="modal delete-modal">
        <div class="modal-header">
          <h2 class="modal-title">מחיקת הזמנה</h2>
          <button class="modal-close" @click="closeDeleteModal">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="deleteError" class="msg msg-error">{{ deleteError }}</div>

          <div class="delete-info">
            <p class="delete-order-number">
              הזמנה מספר: <strong>{{ formatOrderNumber(deleteModalOrder?.orderNumber) }}</strong>
            </p>
            <p class="delete-warning">האם אתה בטוח שברצונך למחוק הזמנה זו?</p>
            <p class="delete-note">פעולה זו לא ניתנת לביטול.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" :disabled="deleteLoading" @click="closeDeleteModal">
            ביטול
          </button>
          <button class="btn-delete-confirm" :disabled="deleteLoading" @click="confirmDelete">
            <svg
              v-if="deleteLoading"
              class="spin"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <span>{{ deleteLoading ? 'מוחק...' : 'מחק הזמנה' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.my-orders-view {
  min-height: 100%;
  background: var(--bg-secondary);
}

/* Header */
.orders-header {
  direction: rtl;
  display: flex;
  align-items: stretch;
  background: var(--gradient-header);
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
  min-height: 56px;
}

.header-spacer {
  width: 100px;
}

.page-title {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--spacing-lg);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.back-short {
  display: none;
}

/* Client Banner */
.client-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%);
  border-bottom: 1px solid var(--border-color);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.client-banner svg {
  color: var(--color-primary);
}

.client-makat {
  color: var(--text-muted);
}

/* Filters Bar */
.filters-bar {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.search-wrapper {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.filter-input {
  width: 100%;
  padding: 8px 34px 8px 30px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  background: var(--bg-primary);
  outline: none;
  transition: border-color var(--transition-fast);
}

.filter-input:focus {
  border-color: var(--color-primary);
}

.filter-input::placeholder {
  color: var(--text-muted);
}

.clear-search {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: var(--bg-tertiary);
  border: none;
  border-radius: 50%;
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.clear-search:hover {
  background: var(--color-gray-300);
  color: var(--text-primary);
}

.select-wrapper {
  position: relative;
  min-width: 150px;
}

.filter-select {
  width: 100%;
  padding: 8px 12px 8px 28px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  background: var(--bg-primary);
  appearance: none;
  cursor: pointer;
  outline: none;
  transition: border-color var(--transition-fast);
}

.filter-select:focus {
  border-color: var(--color-primary);
}

.select-chevron {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.results-count {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  white-space: nowrap;
}

.clear-filters-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: var(--border-radius);
  color: #dc2626;
  font-size: var(--font-size-xs);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.clear-filters-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

/* No Filter Results */
.no-filter-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl) var(--spacing-md);
  text-align: center;
}

.no-filter-results svg {
  color: var(--text-muted);
  opacity: 0.3;
}

.no-filter-results p {
  color: var(--text-muted);
  margin: 0;
}

/* Sortable Table Headers */
.th-sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
  white-space: nowrap;
}

.th-sortable:hover {
  color: var(--text-primary);
}

.th-active {
  color: var(--color-primary);
}

.sort-icon {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-right: 2px;
}

/* Content */
.orders-content {
  padding: var(--spacing-lg);
  max-width: 1200px;
  margin: 0 auto;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl) * 2;
  color: var(--text-muted);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl) * 2;
  text-align: center;
  color: var(--color-error);
}

.error-container svg {
  opacity: 0.5;
}

.retry-btn {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--color-error);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.retry-btn:hover {
  opacity: 0.9;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl) * 2;
  text-align: center;
}

.empty-state svg {
  color: var(--text-muted);
  opacity: 0.3;
}

.empty-state h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
}

.empty-state p {
  color: var(--text-muted);
  margin: 0;
}

.action-btn {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--gradient-primary);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

/* Orders Table */
.orders-table-wrapper {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.orders-table {
  width: 100%;
  border-collapse: collapse;
}

.orders-table th {
  padding: var(--spacing-md);
  text-align: right;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.orders-table td {
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
}

.order-row:last-child td {
  border-bottom: none;
}

.order-row:hover {
  background: var(--bg-secondary);
}

.order-row.clickable {
  cursor: pointer;
  transition: all var(--transition-fast);
}

.order-row.clickable:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(99, 102, 241, 0.02) 100%);
}

/* Order Number */
.order-number-cell {
  font-family: monospace;
}

.order-number {
  display: inline-block;
  padding: 4px 8px;
  background: var(--bg-tertiary);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

/* Order Date */
.order-date-cell {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

/* Order Items */
.order-items-cell {
  font-size: var(--font-size-sm);
}

.items-count {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.items-label {
  color: var(--text-secondary);
  margin-right: 4px;
}

.unique-count {
  color: var(--text-muted);
  font-size: var(--font-size-xs);
}

/* Order Total */
.order-total-cell {
  font-size: var(--font-size-base);
}

.total-amount {
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.currency {
  color: var(--text-muted);
  margin-right: 2px;
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.status-new {
  background: rgba(34, 197, 94, 0.1);
  color: #15803d;
}

.status-priority {
  background: rgba(40, 53, 72, 0.1);
  color: var(--color-primary);
}

.status-pending {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.status-paid {
  background: rgba(34, 197, 94, 0.1);
  color: #15803d;
}

.status-processing {
  background: rgba(59, 130, 246, 0.1);
  color: #1d4ed8;
}

.status-shipped {
  background: rgba(139, 92, 246, 0.1);
  color: #7c3aed;
}

.status-delivered {
  background: rgba(34, 197, 94, 0.15);
  color: #059669;
}

.status-cancelled {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

/* Payment Button */
.payment-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.payment-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.no-action {
  color: var(--text-muted);
}

/* Actions Group */
.actions-group {
  display: flex;
  gap: 8px;
}

/* Delete Button */
.delete-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: var(--border-radius);
  color: #dc2626;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.delete-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.5);
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
}

.modal {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  max-width: 400px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  background: rgba(239, 68, 68, 0.1);
}

.modal-title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: #dc2626;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.modal-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-body {
  padding: var(--spacing-lg);
}

.msg-error {
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: var(--border-radius);
  color: #dc2626;
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-md);
}

.delete-info {
  text-align: center;
}

.delete-order-number {
  font-size: var(--font-size-base);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md);
}

.delete-warning {
  font-size: var(--font-size-base);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs);
}

.delete-note {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  border-top: 1px solid var(--border-color);
}

.btn-cancel {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-cancel:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-delete-confirm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: var(--spacing-sm) var(--spacing-lg);
  background: #dc2626;
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-delete-confirm:hover:not(:disabled) {
  background: #b91c1c;
}

.btn-delete-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-delete-confirm .spin,
.payment-btn .spin {
  animation: spin 1s linear infinite;
}

.payment-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Mobile Cards Layout */
.orders-cards {
  flex-direction: column;
  gap: var(--spacing-md);
}

/* Mobile/Desktop visibility - must come after layout rules */
.mobile-only {
  display: none !important;
}

.desktop-only {
  display: block;
}

.order-card {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.order-card.clickable {
  cursor: pointer;
  transition: all var(--transition-fast);
}

.order-card.clickable:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.order-card.clickable:active {
  transform: translateY(0);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.card-body {
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-label {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.card-value {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

.card-row.total-row {
  margin-top: var(--spacing-xs);
  padding-top: var(--spacing-sm);
  border-top: 1px dashed var(--border-color);
}

.card-row.total-row .card-value {
  font-size: var(--font-size-base);
}

.card-footer {
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-md);
}

.card-footer .payment-btn,
.card-footer .delete-btn {
  flex: 1;
  justify-content: center;
  padding: var(--spacing-sm) var(--spacing-md);
}

/* Responsive */
@media (max-width: 768px) {
  .my-orders-view {
    overflow-x: hidden;
  }

  .mobile-only {
    display: flex !important;
  }

  .desktop-only {
    display: none !important;
  }

  .orders-header {
    min-height: 48px;
  }

  .header-spacer {
    display: none;
  }

  .page-title {
    font-size: var(--font-size-lg);
  }

  .back-btn {
    padding: 0 var(--spacing-md);
  }

  .header-spacer {
    display: none;
  }

  .back-full {
    display: none;
  }

  .back-short {
    display: inline;
  }

  .orders-content {
    padding: var(--spacing-md);
  }

  .filters-bar {
    padding: var(--spacing-sm);
    gap: var(--spacing-xs);
  }

  .search-wrapper {
    min-width: 100%;
  }

  .select-wrapper {
    flex: 1;
    min-width: 120px;
  }

  .results-count {
    flex-basis: 100%;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .client-banner {
    font-size: var(--font-size-xs);
  }

  .orders-content {
    padding: var(--spacing-sm);
  }

  .card-header {
    padding: var(--spacing-xs) var(--spacing-sm);
  }

  .card-body {
    padding: var(--spacing-sm);
  }

  .order-number {
    font-size: var(--font-size-xs);
    padding: 2px 6px;
  }

  .status-badge {
    font-size: 10px;
    padding: 2px 8px;
  }
}
</style>
